import { animate, style, transition, trigger } from '@angular/animations';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import {
  AggregatedPlaylistDTO,
  API,
  ChannelFullDTO,
  ClientDTO,
  CullDTO,
  LocationFullDTO,
  PlanningDTO,
  SharedClientCentricContentShareAssignmentDTO,
  SharedClientNetworkAccessDTO,
  SharedPlaybackScheduleDTO,
  SharedPlaylistDTO,
  SharedSetlistDTO,
} from 'src/app/services/api/api';
import { SbLocalDateTime } from 'src/app/services/api/api-date-time';
import { ErrorService } from '../../../services/error.service';
import { TranslateService } from '@ngx-translate/core';
import { MsOptions } from '../../../services/global/global-interfaces';
import { PermissionService } from '../../../services/permission.service';
import { DateTime } from 'luxon';
import { QueryParamService } from '../../../services/lists/query-params';
import { forkJoin, iif, of, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { StringTransformationService } from 'src/app/services/transformation-helper/string-transformation.service';
import { UtilService } from '../../../services/util.service';

@Component({
  selector: 'sb-page-network-allocation',
  templateUrl: './network-allocation.component.html',
  styleUrls: ['./network-allocation.component.scss'],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [style({ opacity: 0 }), animate(250, style({ opacity: 1 }))]),
      transition(':leave', [animate(500, style({ opacity: 0 }))]),
    ]),
  ],
  standalone: false,
})
export class NetworkAllocationComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly stringTransformation = inject(StringTransformationService);

  sharedSetlists: SharedSetlistDTO[] = [];
  sharedContentShares: SharedClientCentricContentShareAssignmentDTO[] = [];
  sharedPlaybackSchedules: SharedPlaybackScheduleDTO[] = [];
  sharedPlaylists: SharedPlaylistDTO[] | AggregatedPlaylistDTO[] = [];
  sharedNetworkAccess: SharedClientNetworkAccessDTO[] = [];

  subs: Subscription[] = [];

  debounceTimer: ReturnType<typeof setTimeout> = null;

  loadingSetlist: boolean;
  loadingPlaylist: boolean;
  loadingContentShare: boolean;
  loadingPlaybackSchedule: boolean;
  loadingNetworkAccess: boolean;
  loading: boolean;
  // multiselect values
  culls: CullDTO[] = [];
  plannings: PlanningDTO[] = [];
  locations: LocationFullDTO[] = [];
  channels: ChannelFullDTO[] = [];
  advertisers: ClientDTO[] = [];
  // multiselect selected values
  filteredLocations: LocationFullDTO[] = [];
  filteredCulls: CullDTO[] = [];
  filteredPlanning: PlanningDTO = null;
  filteredChannels: ChannelFullDTO[] = [];
  filteredAdvertisers: ClientDTO[] = [];
  // multiselect options
  msCullOptions: MsOptions;
  msPlanningOptions: MsOptions;
  msLocationOptions: MsOptions;
  msChannelOptions: MsOptions;
  msAdvertiserOptions: MsOptions;

  useAggregated: boolean;
  queryParamsParsed: boolean;

  // time
  from?: SbLocalDateTime;
  till?: SbLocalDateTime;
  // inputs
  screen?: string;
  device?: string;

  constructor(
    private readonly api: API,
    private readonly util: UtilService,
    public readonly errorHandler: ErrorService,
    public readonly translate: TranslateService,
    public readonly queryParamService: QueryParamService,
    public readonly permissionService: PermissionService
  ) {
    this.useAggregated = true;
    this.queryParamsParsed = false;
    this.loadingSetlist = true;
    this.loadingPlaylist = true;
    this.loadingContentShare = true;
    this.loadingPlaybackSchedule = true;
    this.loadingNetworkAccess = true;
    this.loading = false;
  }

  ngOnInit() {
    this.parseQueryParams().then(() => {
      this.queryParamsParsed = true;
    });
  }

  parseQueryParams(): Promise<void> {
    return new Promise(res => {
      this.screen = this.queryParamService.getQueryParam('screen');
      this.device = this.queryParamService.getQueryParam('device');
      this.parseDateParams();
      this.parseFilterQueryParams().then(res);
    });
  }

  parseDateParams() {
    if (this.queryParamService.getQueryParam('from') && this.queryParamService.getQueryParam('till')) {
      this.from = new SbLocalDateTime(DateTime.fromISO(this.queryParamService.getQueryParam('from')));
      this.till = new SbLocalDateTime(DateTime.fromISO(this.queryParamService.getQueryParam('till')));
    }
  }

  parseFilterQueryParams(): Promise<void> {
    return new Promise(res => {
      const planningQp = this.queryParamService.getQueryParam('planning');
      const cullsQp = this.queryParamService.getQueryParam('culls');
      const channelsQp = this.queryParamService.getQueryParam('channels');
      const advertisersQp = this.queryParamService.getQueryParam('advertisers');

      let cullIds: number[] = [];
      let channelIds: number[] = [];
      let advertiserIds: number[] = [];
      if (cullsQp && cullsQp.length > 0) {
        cullIds = JSON.parse(cullsQp);
      }
      if (channelsQp && channelsQp.length > 0) {
        channelIds = JSON.parse(channelsQp);
      }
      if (advertisersQp && advertisersQp.length > 0) {
        advertiserIds = JSON.parse(advertisersQp);
      }

      forkJoin({
        plannings:
          planningQp && !isNaN(Number(planningQp)) ? this.api.ds_cm_Planning_retrieveAll({ id: [+planningQp] }) : of([] as PlanningDTO[]),
        culls: cullIds && cullIds.length > 0 ? this.api.cs_Cull_retrieveAll({ id: cullIds }) : of([] as CullDTO[]),
        channels: channelIds && channelIds.length > 0 ? this.api.ss_nm_Channel_retrieveAll({ id: channelIds }) : of([] as ChannelFullDTO[]),
        advertisers:
          advertiserIds && advertiserIds.length > 0
            ? this.api.shared_Client_retrieveConnectedClients({ id: advertiserIds })
            : of([] as ClientDTO[]),
      })
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(resultArray => {
          this.filteredCulls = resultArray?.culls ?? [];
          this.filteredPlanning = resultArray?.plannings[0] ?? null;
          this.filteredChannels = resultArray?.channels ?? [];
          this.filteredAdvertisers = resultArray?.advertisers ?? [];
          res();
        });
    });
  }

  refreshQueryParams() {
    if (this.filteredPlanning) {
      this.queryParamService.setQueryParam('planning', this.filteredPlanning.id.toString());
    } else {
      this.queryParamService.clearQueryParam('planning');
    }
    if (this.filteredCulls?.length > 0) {
      this.queryParamService.setQueryParam('culls', JSON.stringify(this.util.arrayToIdArray(this.filteredCulls)));
    } else {
      this.queryParamService.clearQueryParam('culls');
    }
    if (this.filteredChannels?.length > 0) {
      this.queryParamService.setQueryParam('channels', JSON.stringify(this.util.arrayToIdArray(this.filteredChannels)));
    } else {
      this.queryParamService.clearQueryParam('channels');
    }
    if (this.filteredAdvertisers?.length > 0) {
      this.queryParamService.setQueryParam('advertisers', JSON.stringify(this.util.arrayToIdArray(this.filteredAdvertisers)));
    } else {
      this.queryParamService.clearQueryParam('advertisers');
    }
    if (this.screen) {
      this.queryParamService.setQueryParam('screen', this.screen);
    } else {
      this.queryParamService.clearQueryParam('screen');
    }
    if (this.device) {
      this.queryParamService.setQueryParam('device', this.device);
    } else {
      this.queryParamService.clearQueryParam('device');
    }
    if (this.from) {
      this.queryParamService.setQueryParam('from', this.from.toString());
    } else {
      this.queryParamService.clearQueryParam('from');
    }
    if (this.till) {
      this.queryParamService.setQueryParam('till', this.till.toString());
    } else {
      this.queryParamService.clearQueryParam('till');
    }
  }

  refresh() {
    this.loading = true;
    this.requestDebounced();
  }

  getInfoText(): string {
    if (this.loadingSetlist && this.loadingPlaylist) {
      return '';
    }
    let str = '';
    if (this.sharedSetlists) {
      str += this.sharedSetlists.length ?? 0 + ' Setlists | ';
    }
    if (this.sharedPlaylists) {
      str += this.sharedPlaylists.length ?? 0 + ' Playlists';
    }
    return str;
  }

  requestDebounced() {
    if (this.queryParamsParsed) {
      this.refreshQueryParams();
    }
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.clearSubs();
    this.debounceTimer = setTimeout(() => {
      this.request();
    }, 500);
  }

  request() {
    if ((this.from !== null) === (this.till !== null) && (this.till && this.from ? this.till?.value >= this.from?.value : true)) {
      // both filled or both null
      const defaultTime = new SbLocalDateTime(DateTime.now());
      this.requestSetlists(defaultTime);
      this.requestPlaylists(defaultTime);
      this.requestContentShares(defaultTime);
      this.requestPlaybackSchedules(defaultTime);
      this.requestNetworkAccess(defaultTime);
    } else {
      this.loading = false;
      this.sharedSetlists = [];
      this.sharedPlaylists = [];
      this.sharedNetworkAccess = [];
      this.sharedContentShares = [];
      this.sharedPlaybackSchedules = [];
    }
  }

  clearSubs() {
    if (this.subs.length <= 0) {
      return;
    }
    this.subs.forEach(sub => {
      sub.unsubscribe();
    });
  }

  getLocationIDs() {
    return this.filteredLocations?.length > 0 ? this.filteredLocations.map(location => location.id) : null;
  }

  getChannelIDs() {
    return this.filteredChannels?.length > 0 ? this.filteredChannels.map(channel => channel.id) : null;
  }

  getCullIDs() {
    return this.filteredCulls?.length > 0 ? this.filteredCulls.map(cull => cull.id) : null;
  }

  getAdvertiserIDs() {
    return this.filteredAdvertisers?.length > 0 ? this.filteredAdvertisers.map(advertiser => advertiser.id) : null;
  }

  getScreenIDs() {
    if (!this.screen || this.screen.length === 0) {
      return null;
    }
    const screens = this.screen
      .split(',')
      .map(screen => Number(screen.trim()))
      .filter(Boolean);
    return screens?.length > 0 ? screens : null;
  }

  getDeviceIDs() {
    if (!this.device || this.device.length === 0) {
      return null;
    }
    const devices = this.device
      .split(',')
      .map(device => Number(device.trim()))
      .filter(Boolean);
    return devices?.length > 0 ? devices : null;
  }

  requestSetlists(defaultTime: SbLocalDateTime) {
    this.loadingSetlist = true;
    const sub = this.api
      .ss_nm_Network_retrieveAllSharedSetlists({
        active: true,
        permitted: true,
        planning: this.filteredPlanning ? this.filteredPlanning.id : null,
        from: this.from ? this.from : defaultTime,
        till: this.till ? this.till : defaultTime,
        advertiser: this.getAdvertiserIDs(),
        screen: this.getScreenIDs(),
        device: this.getDeviceIDs(),
        playerCull: this.getCullIDs(),
        location: this.getLocationIDs(),
        channel: this.getChannelIDs(),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        take(1),
        finalize(() => {
          this.loadingSetlist = false;
        })
      )
      .subscribe({
        next: (setlists: SharedSetlistDTO[]) => {
          this.sharedSetlists = setlists;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  requestPlaylists(defaultTime: SbLocalDateTime) {
    this.loadingPlaylist = true;
    const options = {
      active: true,
      permitted: true,
      planning: this.filteredPlanning ? this.filteredPlanning.id : null,
      from: this.from ? this.from : defaultTime,
      till: this.till ? this.till : defaultTime,
      advertiser: this.getAdvertiserIDs(),
      screen: this.getScreenIDs(),
      device: this.getDeviceIDs(),
      playerCull: this.getCullIDs(),
      location: this.getLocationIDs(),
      channel: this.getChannelIDs(),
    };

    const sub = iif(
      () => this.useAggregated,
      this.api.ss_nm_Network_retrieveAllAggregatedPlaylistsByBookedMediumGroup(options),
      this.api.ss_nm_Network_retrieveAllSharedPlaylists(options)
    )
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingPlaylist = false;
        })
      )
      .subscribe({
        next: playlists => {
          this.sharedPlaylists = playlists;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  requestPlaybackSchedules(defaultTime: SbLocalDateTime) {
    this.loadingPlaybackSchedule = true;
    const options = {
      planning: this.filteredPlanning ? this.filteredPlanning.id : null,
      from: this.from ? this.from : defaultTime,
      till: this.till ? this.till : defaultTime,
      advertiser: this.getAdvertiserIDs(),
      screen: this.getScreenIDs(),
      device: this.getDeviceIDs(),
      playerCull: this.getCullIDs(),
      location: this.getLocationIDs(),
      channel: this.getChannelIDs(),
    };

    const sub = this.api
      .ss_nm_Network_retrieveAllSharedPlaybackSchedules(options)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingPlaybackSchedule = false;
        })
      )
      .subscribe({
        next: schedules => {
          this.sharedPlaybackSchedules = schedules;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  requestContentShares(defaultTime: SbLocalDateTime) {
    this.loadingContentShare = true;
    const options = {
      planning: this.filteredPlanning ? this.filteredPlanning.id : null,
      from: this.from ? this.from : defaultTime,
      till: this.till ? this.till : defaultTime,
      advertiser: this.getAdvertiserIDs(),
      screen: this.getScreenIDs(),
      device: this.getDeviceIDs(),
      playerCull: this.getCullIDs(),
      location: this.getLocationIDs(),
      channel: this.getChannelIDs(),
    };

    const sub = this.api
      .ss_nm_Network_retrieveAllSharedContentShareAssignments(options)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingContentShare = false;
        })
      )
      .subscribe({
        next: shares => {
          this.sharedContentShares = shares;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }

  requestNetworkAccess(defaultTime: SbLocalDateTime) {
    this.loadingNetworkAccess = true;
    const options = {
      planning: this.filteredPlanning ? this.filteredPlanning.id : null,
      // from: this.from ? this.from : defaultTime,
      // till: this.till ? this.till : defaultTime,
      advertiser: this.getAdvertiserIDs(),
      screen: this.getScreenIDs(),
      device: this.getDeviceIDs(),
      playerCull: this.getCullIDs(),
      location: this.getLocationIDs(),
      channel: this.getChannelIDs(),
    };

    const sub = this.api
      .ss_nm_Network_retrieveNetworkAccess(options)
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.loadingNetworkAccess = false;
        })
      )
      .subscribe({
        next: accesses => {
          this.sharedNetworkAccess = accesses;
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
    this.subs.push(sub);
  }


  changeFrom(date) {
    this.from = date;
  }

  changeTill(date) {
    this.till = date;
  }

  update($event: any[], type: string) {
    switch (type) {
      case 'PLAYER_CULLS':
        if ($event?.length > 0) {
          this.filteredCulls = [...$event];
        } else {
          this.filteredCulls = null;
        }
        break;
      case 'PLANNINGS':
        if ($event?.length > 0) {
          this.filteredPlanning = $event[0];
        } else {
          this.filteredPlanning = null;
        }
        break;
      case 'LOCATIONS':
        if ($event?.length > 0) {
          this.filteredLocations = [...$event];
        } else {
          this.filteredLocations = [];
        }
        break;
      case 'CHANNELS':
        if ($event?.length > 0) {
          this.filteredChannels = [...$event];
        } else {
          this.filteredChannels = [];
        }
        break;
      case 'ADVERTISERS':
        if ($event?.length > 0) {
          this.filteredAdvertisers = [...$event];
        } else {
          this.filteredAdvertisers = [];
        }
        break;
    }
  }

  msRequest(type: string, options?: MsOptions) {
    if (options) {
      this.setMsOptions(type, options);
    }
    switch (type) {
      case 'PLAYER_CULLS':
        if (this.msCullOptions?.pagination) {
          this.api
            .cs_Cull_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msCullOptions?.search),
                type: 'PlayerCull',
              },
              this.msCullOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (culls: CullDTO[]) => {
                this.culls = culls;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'PLANNINGS':
        if (this.msPlanningOptions?.pagination) {
          this.api
            .ds_cm_Planning_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msPlanningOptions?.search),
              },
              this.msPlanningOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (plannings: PlanningDTO[]) => {
                this.plannings = plannings;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'LOCATIONS':
        if (this.msLocationOptions?.pagination) {
          this.api
            .ss_fm_Location_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msLocationOptions?.search),
              },
              this.msLocationOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (retrievedLocations: LocationFullDTO[]) => {
                this.locations = retrievedLocations;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'CHANNELS':
        if (this.msChannelOptions?.pagination) {
          this.api
            .ss_nm_Channel_retrieveAll(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msChannelOptions?.search),
              },
              this.msChannelOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (retrievedChannels: ChannelFullDTO[]) => {
                this.channels = retrievedChannels;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
      case 'ADVERTISERS':
        if (this.msAdvertiserOptions?.pagination) {
          this.api
            .shared_Client_retrieveConnectedClients(
              {
                search: this.stringTransformation.encodeSearchQueryParam(false, this.msAdvertiserOptions?.search),
              },
              this.msAdvertiserOptions.pagination
            )
            .pipe(take(1), takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: (retrievedClients: ClientDTO[]) => {
                this.advertisers = retrievedClients;
              },
              error: error => {
                this.errorHandler.errorHandler(error);
              },
            });
        }
        break;
    }
  }

  private setMsOptions(type: string, options: MsOptions) {
    switch (type) {
      case 'PLAYER_CULLS':
        this.msCullOptions = options;
        break;
      case 'PLANNINGS':
        this.msPlanningOptions = options;
        break;
      case 'LOCATIONS':
        this.msLocationOptions = options;
        break;
      case 'CHANNELS':
        this.msChannelOptions = options;
        break;
      case 'ADVERTISERS':
        this.msAdvertiserOptions = options;
        break;
    }
  }

  changeAggregationUsage($event: boolean) {
    this.useAggregated = $event;

    this.loadingSetlist = true;
    this.loadingPlaylist = true;
    this.loading = false;
  }
}
